import type {
  IQueryActionContext,
  IQueryActionConfig,
  TQueryAction,
  QueryActionReturn,
} from '~/interfaces/middleware/query-actions';
import { globalKeys } from 'assets/data/global-keys';
import {
  type AccountModals,
  AccountModalsStrings,
  type Modals,
  siteModalsStrings,
} from '~/interfaces/dto/general/modals';
import type {
  RouteLocationNormalized,
  RouteLocationNormalizedGeneric,
} from 'vue-router';
import { loginRoutes, registerRoutes } from 'assets/data/config';

const queryActionsRunning = ref<boolean>(false);

const debugInDev = (
  msg: string,
  additionalInfo: string = 'no additional information',
  msgType: 'error' | 'warn' | 'info' = 'warn',
) => {
  const { $storageService } = useServices();
  const debugEnabled = $storageService.getSessionStorage({
    key: globalKeys.developerConfigKeys.debugQueryStrings,
  });
  if (import.meta.dev && !!debugEnabled) console[msgType](msg, additionalInfo);
};

const handleNavigateQueryTarget = (
  to: RouteLocationNormalizedGeneric,
  value: unknown,
  key: string | undefined,
  emptyMsg: string,
) => {
  const target = String(value);
  if (!target || target.trim().length === 0) {
    debugInDev(emptyMsg);
    return;
  }
  return removeQueryStringAndNavigate(to, key ?? '', target);
};

export const actionHandlersConfig: Record<TQueryAction, IQueryActionConfig> = {
  // Auth-required handlers (sorted alphabetically)
  account: {
    requiresAuth: true,
    handler: async ({ auth, site }, value) => {
      const accountModal = String(value);
      if (!AccountModalsStrings.includes(accountModal as AccountModals)) {
        debugInDev('Invalid account modal type:', accountModal);
        return;
      }
      if (auth.loggedIn)
        auth.setAccountModalPage(accountModal as AccountModals);
      else site.activateModal('login');
    },
  },
  accountRestricted: {
    requiresAuth: true,
    handler: async ({ auth, site }) => {
      if (auth.loggedIn) site.activateModal('accountRestricted');
    },
  },
  deposit: {
    requiresAuth: true,
    handler: async ({ auth, site }) => {
      if (auth.loggedIn) auth.setAccountModalPage('deposit');
      else site.activateModal('login');
    },
  },
  displayAccount: {
    requiresAuth: true,
    handler: async ({ auth, site }) => {
      if (auth.loggedIn) auth.toggleAccountModal(true);
      else site.activateModal('login');
    },
  },
  'gifts-offers': {
    requiresAuth: true,
    handler: async ({ auth, site }) => {
      if (auth.loggedIn) auth.setAccountModalPage('gifts-offers');
      else site.activateModal('login');
    },
  },
  'bonus-wallet': {
    requiresAuth: true,
    handler: async ({ auth, site }) => {
      if (auth.loggedIn) auth.setAccountModalPage('bonus-wallet');
      else site.activateModal('login');
    },
  },
  'document-verification': {
    requiresAuth: true,
    handler: async ({ auth, site }) => {
      if (auth.loggedIn) auth.setAccountModalPage('document-verification');
      else site.activateModal('login');
    },
  },
  login: {
    requiresAuth: true,
    handler: async ({ auth, site, to }, _, key) => {
      if (!auth.loggedIn) {
        if (registerRoutes.includes(to.path))
          return removeQueryStringAndNavigate(to, key, '/login');
        else site.activateModal('login');
      }
    },
  },
  optin: {
    requiresAuth: true,
    handler: async (
      { auth, site, $marketingService, $storageService, localeKey, to },
      value,
    ) => {
      const optInName = String(value);
      if (!optInName || optInName.trim().length === 0) {
        debugInDev('Empty optin name provided');
        return;
      }

      if (auth.loggedIn) {
        await $marketingService
          .setMarketingOptIn({
            vendorAccountID: $storageService.getCookie({
              key: globalKeys.authKeys.userId,
            }),
            optinName: optInName,
            source: String(to.query?.source || 'Web'),
            countryCode: site.getRegionCode,
          })
          .then(() => {
            setTimeout(
              () =>
                site.toggleSiteNotice({
                  heading: localeKey('marketing-opt-in-heading'),
                  content: {
                    data: localeKey('marketing-opt-in-message'),
                  },
                  severity: 'success',
                  enabled: true,
                  timeout: 5000,
                }),
              1000,
            );
          });
      } else site.activateModal('login');
    },
  },
  register: {
    requiresAuth: true,
    handler: async ({ auth, site, to }, _, key) => {
      if (!auth.loggedIn) {
        if (loginRoutes.includes(to.path))
          return removeQueryStringAndNavigate(to, key, '/register');
        else site.activateModal('register');
      }
    },
  },
  'reset-password': {
    requiresAuth: true,
    handler: async ({ auth, site }) => {
      if (auth.loggedIn) auth.setAccountModalPage('password-management');
      else site.activateModal('passwordResetStepOne');
    },
  },
  'sign-up': {
    requiresAuth: true,
    handler: async ({ auth, site, to }, _, key) => {
      if (!auth.loggedIn) {
        if (loginRoutes.includes(to.path))
          return removeQueryStringAndNavigate(to, key, '/register');
        else site.activateModal('register');
      }
    },
  },
  withdrawal: {
    requiresAuth: true,
    handler: async ({ auth, site }) => {
      if (auth.loggedIn) auth.setAccountModalPage('withdrawal');
      else site.activateModal('login');
    },
  },

  // Non-auth handlers (sorted alphabetically)
  action: {
    requiresAuth: false,
    handler: async ({ to }) => {
      if (Object.prototype.hasOwnProperty.call(to.query, 'actionValue'))
        return useMessageHandler(
          to.query as { action: string; actionValue: string },
        );
    },
  },
  authenticate: {
    requiresAuth: false,
    handler: async ({ auth }, value) => {
      if (auth.isLoggedIn || auth.currentAccessToken) return;
      auth.setAccessToken(value as string);
      await auth.setLoggedIn();
    },
  },
  isNativeApp: {
    requiresAuth: false,
    handler: async ({ site }, value) => site.setWebview(!!value),
  },
  'live-chat': {
    requiresAuth: false,
    handler: async () => {
      const { $toggleChat } = useNuxtApp();
      await nextTick(() => $toggleChat());
    },
  },
  modal: {
    requiresAuth: false,
    handler: async ({ site }, value) => {
      const modalName = String(value);
      if (!modalName || modalName.trim().length === 0) {
        debugInDev('Empty modal name provided');
        return;
      }
      if (!siteModalsStrings.includes(modalName as Modals)) {
        debugInDev('Invalid modal name:', modalName);
        return;
      }
      site.activateModal(modalName);
    },
  },
  path: {
    requiresAuth: false,
    handler: async ({ to }, value, key) =>
      handleNavigateQueryTarget(to, value, key, 'Empty path provided'),
  },
  redirect: {
    requiresAuth: false,
    handler: async ({ to }, value, key) =>
      handleNavigateQueryTarget(
        to,
        value,
        key,
        'Empty redirect target provided',
      ),
  },
  session: {
    requiresAuth: false,
    handler: async ({ $authService }, value) => {
      const sessionToken = String(value);
      if (!sessionToken || sessionToken.trim().length === 0) {
        debugInDev('Empty session token provided');
        return;
      }
      $authService.handleLoginSession(sessionToken);
    },
  },
  signupcode: {
    requiresAuth: false,
    handler: async ({ $storageService }, value) => {
      const signupCode = String(value);
      if (!signupCode || signupCode.trim().length === 0) {
        debugInDev('Empty signup code provided');
        return;
      }
      $storageService.setCookie({
        key: globalKeys.marketingKeys.signUpCode,
        data: signupCode,
        expiry: 7,
      });
    },
  },
  theme: {
    requiresAuth: false,
    handler: (_, value) => {
      if (!['light', 'dark'].includes(value as string)) {
        debugInDev(`${value} is not a valid theme.`);
        return;
      }
      const colorMode = useColorMode();
      colorMode.preference = value as string;
    },
  },
  toast: {
    requiresAuth: false,
    handler: async (_, value) => {
      const { $toastService } = useNuxtApp();
      const message = String(value);
      if (!message || message.trim().length === 0) {
        debugInDev('Empty toast message provided');
        return;
      }
      $toastService.generateToast(message);
    },
  },
  webView: {
    requiresAuth: false,
    handler: async ({ site }, value) => site.setWebview(!!value),
  },
};

const actionHandlers = new CaseInsensitiveMap<TQueryAction, IQueryActionConfig>(
  Object.entries(actionHandlersConfig).map(
    ([k, v]) => [k as TQueryAction, v] as [TQueryAction, IQueryActionConfig],
  ),
);

const createContext = (
  to: RouteLocationNormalizedGeneric,
): IQueryActionContext => {
  const auth = useAuthStore();
  const site = useSiteStore();
  const { $authService, $storageService, $marketingService } = useServices();
  const { localeKey } = useLocaleUtils();
  return {
    to,
    auth,
    site,
    $authService,
    $storageService,
    $marketingService,
    localeKey,
  };
};

const processQueryAction = async (
  key: TQueryAction,
  value: unknown,
  ctx: IQueryActionContext,
): Promise<QueryActionReturn> => {
  const entry = actionHandlers.get(key);
  if (!entry) {
    debugInDev(`Query action "${key}" not found`, '', 'error');
    return;
  }
  try {
    return await entry.handler(ctx, value, key);
  } catch (e) {
    debugInDev(`Query action "${key}" failed`, e, 'error');
    return;
  }
};

export const removeQueryStringAndNavigate = (
  to: RouteLocationNormalizedGeneric,
  key: string,
  target?: string,
) => {
  const updateQuery = { ...to.query };
  delete updateQuery[key];
  if (key.toLowerCase() === 'action') delete updateQuery['actionValue'];
  if (key.toLowerCase() === 'path') delete updateQuery['referrer'];
  queryActionsRunning.value = false;
  return !!target
    ? navigateTo({ path: target, query: updateQuery, replace: true })
    : navigateTo({ query: updateQuery, replace: true });
};

export const useQueryActions = async (to: RouteLocationNormalized) => {
  if (!queryActionsRunning.value) {
    const ctx = createContext(to);

    queryActionsRunning.value = true;

    const requiresAuth = Object.keys(to.query).some((rawKey) => {
      const normalizedKey = useSlugify(rawKey) as TQueryAction;
      const handler = actionHandlers.get(normalizedKey);
      return !!handler?.requiresAuth;
    });

    const tokenInCookie = ctx.$storageService.getCookie({
      key: globalKeys.authKeys.accessToken,
    });

    if (requiresAuth && !!tokenInCookie && !ctx.auth.currentAccessToken) {
      await ctx.auth.initAuth();
    }

    for (const rawKey of Object.keys(to.query)) {
      const normalizedKey = useSlugify(rawKey) as TQueryAction;
      const value = to.query[rawKey];
      const result = await processQueryAction(normalizedKey, value, ctx);
      const handler = actionHandlers.get(normalizedKey);
      const shouldDelete =
        !['accountrestricted', 'modal'].includes(normalizedKey) && !!handler;
      if (!!result) return result;
      else if (shouldDelete && !!ctx.auth.isLoggedIn)
        return removeQueryStringAndNavigate(to, rawKey, to.path);
      else if (shouldDelete && !handler.requiresAuth)
        return removeQueryStringAndNavigate(to, rawKey, to.path);
    }
    queryActionsRunning.value = false;
  }
};
